import { useGeckoAuth } from 'features/auth'
import { RolodexContext } from 'features/rolodex/RolodexProvider'
import React from 'react'

/**
 * Returns a callback that asks Rolodex to re-run document processing for the
 * given entry ids via `POST /api/v2/files/doc-proc/reprocess`.
 *
 * The endpoint accepts a `LoadOptions` body; we target specific documents with
 * `ids`. `limit` must be set to at least `ids.length` because `LoadOptions.limit`
 * defaults to 10 on the backend and would otherwise truncate the entry list.
 *
 * The backend only re-queues `.pdf` files that are not already ignored/pending
 * (so `failed` documents are eligible) and sets them back to `pending`.
 *
 * Requires `gecko:rolodex:admin` plus edit access on each entry on the backend;
 * callers should handle a 403 for users without those permissions.
 */
export function useReprocessDocuments(): (entryIds: string[]) => Promise<void> {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { apiURL } = React.useContext(RolodexContext)

  return React.useCallback(
    async (entryIds) => {
      if (!isAuthenticated) {
        throw new Error('User is not authenticated')
      }
      if (entryIds.length === 0) {
        throw new Error('No documents to reprocess')
      }

      await axios.post(`${apiURL}/api/v2/files/doc-proc/reprocess`, {
        ids: entryIds,
        limit: entryIds.length,
      })
    },
    [apiURL, axios, isAuthenticated]
  )
}
